/** table[is="lag-table"]
 *
 *  MODIFIER CSS CLASSES (see example usage):
 *      sortup (on th element within thead)
 *      sortdn (on th element within thead)
 *      pinned (on th AND td, usually all th/td cells in a column would either all be pinned or none would be pinned)
 *      icons (on th AND td, used to size and align icons columns with icons)
 *      striked (on tr element within tbody, useful to indicate disabled/inactive rows)
 *      textwrap (on th AND td elements within tbody, useful when you're displaying lots of text in a column and want it to wrap)
 *  
 *  EXAMPLE USAGE:
 *      <lag-scroller>
 *      <table is="lag-table">
 *          <caption>Optional table caption</caption>
 *          <thead>
 *              <tr>
 *                  <th><span class="vertical">Heading 1, vertical text (class=vertical must be on sub element, eg span)</span></th>
 *                  <th class="textwrap">Use TH tags not TD tags for heading cells</th>
 *                  <th class="sortup">Heading 3 with sort-up arrow</th>
 *                  <th class="sortdn pinned">Heading 4 with sort-down arrow, and pinned (ie wont scroll horizontall)</th>
 *              </tr>
 *          </thead>
 *          <tbody>
 *              <tr>
 *                  <td>Table body row cell 1</td>
 *                  <td class="textwrap">Use TD tags for body cells as normal. The 'textwrap' class allows text wrapping and sets a medium/small min-width for the cell, if you need a bigger cell width then wrap your content in a span and set its width.<td>
 *                  <td>See html table documentation for how to use all its native features</td>
 *                  <td class="pinned">Pinned cell means it wont scroll horizontally, useful for action buttons or row totals or stuff like that</td>
 *              </tr>
 *          </tbody>
 *          <tfoot>
 *              <tr>
 *                  <td>Optional footer could be useful for value totals or something</td>
 *                  <td class="textwrap">We probably wont need to use tfoot often, but could be useful sometimes</td>
 *                  <td>Blah blah blah</td>
 *                  <td class="pinned">Pinned footer cell</td>
 *              </tr>
 *          </tfoot>
 *      </table>
 *      </lag-scroller>
 */

import { afterNextRender } from '@polymer/polymer/lib/utils/render-status.js';
import { defineElement } from '../../utility/defineElement.js';
import { debounce } from '../../utility/debounce.js';

const PINNED = 'pinned';
const REFRESH_DEBOUNCE_PERIOD = 200; // ms
const INIT_MAX_ATTEMPTS = 10; // Num times before giving up.
const INIT_ATTEMPT_DELAY = 50; // ms

defineElement(class extends HTMLTableElement {

    static get is() { return 'lag-table'; }

    connectedCallback() {
        this._boundRefresh = this.refresh.bind(this);
        this._wireup(); // Wireup events so future changes will call refresh.
    }
    ready() {
        super.ready();

        afterNextRender(this, _ => { this.refresh(); }); // Initial setup of table cells.
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this._boundRefresh);
    }

    _wireup() {
        // ...on resize.
        window.addEventListener('resize', this._boundRefresh);

        // ...on content mutation.
        try {
            const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
            new MutationObserver(_ => { this.refresh(); }).observe(this, { characterData: true, childList: true, subtree: true });
        }
        catch (_ex) {
            // Some older browsers don't support MutationObserver,
            // instead we will use slotchange event, though this event only detects node removal/addition and
            // not content change (eg innerText/innerHTML).
            this.$.slot.addEventListener('slotchange', this._boundRefresh);
        }
    }

    refresh(attemptCount) {
        this._debouncer_refresh = debounce(this._debouncer_refresh, REFRESH_DEBOUNCE_PERIOD).then(_ => {
            // Locked or no rows, exit.
            if (this._refresh_lock || !this.rows || !this.rows.length)
                return;

            // If table doesn't have a width then layout/paint hasn't happened,
            // try refresh in a bit and abort this attempt.
            if (!this.clientWidth && !(attemptCount > INIT_MAX_ATTEMPTS)) { // attemptCount may be undefined so cant do (attemptCount < max)
                // Content not yet ready, so try again in a bit...
                setTimeout(() => this.refresh((attemptCount || 0) + 1), INIT_ATTEMPT_DELAY);
                return; // Don't want to continue.
            }

            // Need a 'lock' otherwise the _adjustPinnedCells will cause MutationObserver events which could cause issues.
            this._refresh_lock = true;

            // Iterate through each row (in all of thead,tbody,tfoot) and then through each of their cells...
            for (const row of this.rows) {
                const cells = row.cells;

                // Iterate the row's cells left-to-right and then right-to-left assigning right/left positions so it pins/sticks (position:sticky).
                this._adjustPinnedCells(cells, 'left');
                this._adjustPinnedCells([...cells].slice().reverse(), 'right');
            }

            this._refresh_lock = false;
        });
    }
    _adjustPinnedCells(cells, style) {
        let offset = 0;

        for (const cell of cells) {
            // If the cell is 'pinned' then we assign the positioning style.
            if (cell.classList.contains(PINNED)) {
                cell.style[style] = offset + 'px'; // Adjust the point at which it will pin/stick (position:sticky).
                offset += cell.offsetWidth; // Add offset for (possible) use by next cell.
            }
        }
    }

}, { extends: 'table' });

/* TODO: resizable lag-table columns
 * Below is a first-attempt at resizable columns (copied from a test and cannot be applied to this component without adjustment),
 * it however did not work (could expand but not contract column smaller than content).

(function () {
    var thElm;
    var startOffset;

    Array.prototype.forEach.call(
      document.querySelectorAll("table th"),
      function (th) {
        th.style.position = 'relative';

        var grip = document.createElement('div');
        grip.innerHTML = "&nbsp;";
        grip.style.top = 0;
        grip.style.right = 0;
        grip.style.bottom = 0;
        grip.style.width = '5px';
        grip.style.backgroundColor = '#ddd';
        grip.style.position = 'absolute';
        grip.style.cursor = 'col-resize';
        grip.addEventListener('mousedown', function (e) {
            thElm = th;
            startOffset = th.offsetWidth - e.pageX;
        });

        th.appendChild(grip);
      });

    document.addEventListener('mousemove', function (e) {
      if (thElm) {
        thElm.style.maxWidth =
        thElm.style.minWidth =
        thElm.style.width = startOffset + e.pageX + 'px';
        console.log('w:' + thElm.style.width + ', o:' + (startOffset + e.pageX));
      }
    });

    document.addEventListener('mouseup', function () {
        thElm = undefined;
    });
})();
*/